// api.ts — TanStack Query hooks for the Master Data module.
// Wired to real backend: /api/master-data/* and /api/identity/stores/
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch, apiFetchList, getAuthToken } from '@/api/client';
import { resolvePath, resolveDetailPath, useRealApi } from '@/api/paths';
import {
  unwrapPage,
  snapshotToProduct,
  storageBinToBin,
  storeDtoToStore,
  stationDtoToStation,
  productToCatalogPayload,
  binToStorageBinPayload,
} from '@/api/adapters';
import type { Station, Policy, BulkJob } from '@/api/adapters';
import type { Store } from '@/domain/types';
import { useStore } from '@/store/store';
import type { Product, Bin } from '@/domain/types';

export const mdKeys = {
  catalog: () => ['master-data', 'catalog'] as const,
  catalogItem: (id: string) => ['master-data', 'catalog', id] as const,
  bins: (store: string) => ['master-data', 'bins', store] as const,
  stations: (store: string) => ['master-data', 'stations', store] as const,
  policies: () => ['master-data', 'policies'] as const,
  bulkJobs: () => ['master-data', 'bulk-jobs'] as const,
  stores: () => ['master-data', 'stores'] as const,
  manufacturers: () => ['master-data', 'manufacturers'] as const,
};

// ─── Catalog (Products) ────────────────────────────────────────────────────

export interface CatalogPage {
  products: Product[];
  count: number;
  next: string | null;
  previous: string | null;
}

export function useCatalog(
  opts: {
    page?: number;
    pageSize?: number;
    search?: string;
    isActive?: boolean;
    source?: string;
    level?: string;
    catalogKind?: 'medicines' | 'packs' | 'appliances' | 'products';
  } = {},
) {
  const { page = 1, pageSize = 50, search, isActive, source, level, catalogKind } = opts;
  const resolved = resolvePath('/products');
  return useQuery({
    queryKey: [
      ...mdKeys.catalog(),
      page,
      pageSize,
      search ?? '',
      isActive ?? '',
      source ?? '',
      level ?? '',
      catalogKind ?? '',
    ],
    queryFn: async (): Promise<CatalogPage> => {
      if (!resolved) return { products: [], count: 0, next: null, previous: null };
      const query: Record<string, string | number | boolean | undefined> = {
        page,
        page_size: pageSize,
      };
      if (search) query.search = search;
      if (isActive !== undefined) query.is_active = isActive;
      if (source) query.source = source;
      if (level) query.level = level;
      if (catalogKind) query.catalog_kind = catalogKind;
      // server-paginated on purpose: this hook drives a real pager (page/
      // pageSize opts) over the 17k-row catalogue — do not fetch-all here
      const data = await apiFetch<unknown>(resolved.path, { query });
      if (useRealApi) {
        const envelope = data as {
          count: number;
          next: string | null;
          previous: string | null;
          results: unknown[];
        };
        return {
          products: (envelope.results ?? []).map(snapshotToProduct),
          count: envelope.count ?? 0,
          next: envelope.next,
          previous: envelope.previous,
        };
      }
      const arr = data as Product[];
      return { products: arr, count: arr.length, next: null, previous: null };
    },
    enabled: resolved !== null,
    placeholderData: (prev) => prev,
  });
}

/** Children of a VMPP pack family — the manufacturer AMPP packs + linked C+D products.
 *  Fetched lazily when a family row is expanded in the catalogue. */
export function useCatalogChildren(vmppId: string | undefined, enabled: boolean) {
  const resolved = resolvePath('/products');
  return useQuery({
    queryKey: [...mdKeys.catalog(), 'children', vmppId ?? ''],
    queryFn: async (): Promise<Product[]> => {
      if (!resolved || !vmppId) return [];
      const data = await apiFetch<unknown>(resolved.path, {
        query: { parent_vmpp: vmppId, page_size: 200 },
      });
      if (useRealApi) {
        const env = data as { results?: unknown[] };
        return (env.results ?? []).map(snapshotToProduct);
      }
      return data as Product[];
    },
    enabled: enabled && !!vmppId && resolved !== null,
  });
}

/** The VMPP pack-families under a generic product (VMP) — the middle layer of the
 *  VMP → VMPP → AMPP drill-down (?parent_vmp=). */
export function useCatalogVmpFamilies(vmpId: string | undefined, enabled: boolean) {
  const resolved = resolvePath('/products');
  return useQuery({
    queryKey: [...mdKeys.catalog(), 'vmp-families', vmpId ?? ''],
    queryFn: async (): Promise<Product[]> => {
      if (!resolved || !vmpId) return [];
      const data = await apiFetch<unknown>(resolved.path, {
        query: { parent_vmp: vmpId, page_size: 200 },
      });
      if (useRealApi) {
        const env = data as { results?: unknown[] };
        return (env.results ?? []).map(snapshotToProduct);
      }
      return data as Product[];
    },
    enabled: enabled && !!vmpId && resolved !== null,
  });
}

export function useCatalogItem(id: string | undefined) {
  return useQuery({
    queryKey: mdKeys.catalogItem(id ?? ''),
    queryFn: async () => {
      const path = useRealApi ? resolveDetailPath(`/products/${id}`) : `/products/${id}`;
      if (!path) return undefined;
      const raw = await apiFetch<unknown>(path);
      return useRealApi ? snapshotToProduct(raw) : (raw as Product);
    },
    enabled: !!id,
  });
}

export function useCreateCatalogItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (product: Partial<Product>) => {
      // CatalogItem CRUD lives at /catalog/ — /products/ is the read-only
      // ProductSnapshot projection and rejects POST (405).
      const path = useRealApi ? '/api/master-data/catalog/' : '/products';
      const body = useRealApi ? productToCatalogPayload(product) : product;
      return apiFetch<Product>(path, { method: 'POST', body });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mdKeys.catalog() }),
  });
}

export function useUpdateCatalogItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<Product> }) => {
      const path = useRealApi ? resolveDetailPath(`/products/${id}`) : `/products/${id}`;
      if (!path) return Promise.reject(new Error('Path not mapped'));
      const body = useRealApi ? productToCatalogPayload(patch) : patch;
      return apiFetch<Product>(path, { method: 'PATCH', body });
    },
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: mdKeys.catalog() });
      qc.invalidateQueries({ queryKey: mdKeys.catalogItem(id) });
    },
  });
}

export function useDeleteCatalogItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      const path = useRealApi ? resolveDetailPath(`/products/${id}`) : `/products/${id}`;
      if (!path) return Promise.reject(new Error('Path not mapped'));
      return apiFetch<void>(path, { method: 'DELETE' });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mdKeys.catalog() }),
  });
}

// ─── Tenant-local (store-added) products ────────────────────────────────────
// These live in `tenant_product` (snapshot NULL) — scoped to tenant + store, never touching the
// shared dm+d/C+D catalogue. This is where "Add Product" writes and the only thing editable.

const TP_PATH = '/api/master-data/tenant-products/';

interface TenantProductDto {
  id: string;
  store_id: string;
  internal_sku: string;
  snapshot: string | null;
  snapshot_name: string | null;
  is_local: boolean;
  name: string;
  manufacturer: string;
  legal_category: string;
  cd_schedule: string;
  is_active: boolean;
  preferred_supplier: string;
  tenant_price: string | null;
}

/** Map a tenant-local product row onto the shared Product UI shape (source = 'local'). */
function tenantProductToProduct(dto: TenantProductDto): Product {
  const isCD = dto.cd_schedule !== '0000' && dto.cd_schedule !== '';
  return {
    id: dto.id,
    sku: dto.internal_sku,
    snapshotKey: dto.internal_sku,
    name: dto.name || dto.snapshot_name || dto.internal_sku,
    strength: '',
    form: '',
    pack: '',
    uom: '',
    barcode: '',
    mfr: dto.manufacturer,
    nhs: '',
    cat: '',
    storage: '',
    cd: isCD,
    fridge: false,
    min: 0,
    max: 0,
    status: dto.is_active ? 'Active' : 'Archived',
    legalCategory: dto.legal_category,
    legalCategoryDisplay: dto.legal_category || '',
    cdSchedule: dto.cd_schedule || '0000',
    source: 'local',
  };
}

/** Map the Product-shaped form back onto the tenant-product write payload. */
function productToTenantPayload(p: Partial<Product>, storeId: string) {
  return {
    store_id: storeId,
    name: (p.name ?? '').trim(),
    internal_sku: p.sku?.trim() || undefined,
    manufacturer: p.mfr ?? '',
    // The form captures CD as a boolean; persist a schedule so downstream CD logic works.
    cd_schedule: p.cd ? (p.cdSchedule && p.cdSchedule !== '0000' ? p.cdSchedule : '0002') : '',
    legal_category: p.legalCategory ?? '',
    is_active: p.status ? p.status === 'Active' : true,
  };
}

/** Store-added local products for the active store (few rows → fetched whole, not paginated). */
export function useLocalProducts(
  storeId: string,
  opts: { search?: string; isActive?: boolean } = {},
  enabled = true,
) {
  return useQuery({
    queryKey: [...mdKeys.catalog(), 'local', storeId, opts.search ?? '', opts.isActive ?? ''],
    queryFn: async (): Promise<Product[]> => {
      if (!useRealApi || !storeId) return [];
      const query: Record<string, string | number | boolean | undefined> = {
        local: true,
        store_id: storeId,
        page_size: 200,
      };
      if (opts.search) query.search = opts.search;
      if (opts.isActive !== undefined) query.is_active = opts.isActive;
      const data = await apiFetchList<TenantProductDto>(TP_PATH, { query });
      return data.map(tenantProductToProduct);
    },
    enabled: enabled && useRealApi && !!storeId,
    placeholderData: (prev) => prev,
  });
}

export function useCreateLocalProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ product, storeId }: { product: Partial<Product>; storeId: string }) =>
      apiFetch<TenantProductDto>(TP_PATH, {
        method: 'POST',
        body: productToTenantPayload(product, storeId),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: mdKeys.catalog() }),
  });
}

export function useUpdateLocalProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      patch,
      storeId,
    }: {
      id: string;
      patch: Partial<Product>;
      storeId: string;
    }) =>
      apiFetch<TenantProductDto>(`${TP_PATH}${id}/`, {
        method: 'PATCH',
        body: productToTenantPayload(patch, storeId),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: mdKeys.catalog() }),
  });
}

// ─── Storage Bins ──────────────────────────────────────────────────────────

export function useStorageBins() {
  const { store } = useStore();
  const resolved = resolvePath('/bins');
  return useQuery({
    queryKey: mdKeys.bins(store),
    queryFn: async () => {
      if (!resolved) return [];
      const query = useRealApi
        ? { store_id: store === 'ALL' ? undefined : store, page_size: 200 }
        : { store };
      const data = await apiFetchList<unknown>(resolved.path, { query });
      if (useRealApi) return unwrapPage(data).map(storageBinToBin);
      return data as Bin[];
    },
    enabled: resolved !== null,
  });
}

export function useCreateStorageBin() {
  const qc = useQueryClient();
  const { store } = useStore();
  return useMutation({
    mutationFn: (bin: Partial<Bin>) => {
      const path = useRealApi ? (resolvePath('/bins')?.path ?? '/api/master-data/bins/') : '/bins';
      const body = useRealApi ? binToStorageBinPayload(bin, store) : bin;
      return apiFetch<Bin>(path, { method: 'POST', body });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mdKeys.bins(store) }),
  });
}

export function useUpdateStorageBin() {
  const qc = useQueryClient();
  const { store } = useStore();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<Bin> }) => {
      const path = useRealApi ? resolveDetailPath(`/bins/${id}`) : `/bins/${id}`;
      if (!path) return Promise.reject(new Error('Path not mapped'));
      const body = useRealApi ? binToStorageBinPayload(patch, store) : patch;
      return apiFetch<Bin>(path, { method: 'PATCH', body });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mdKeys.bins(store) }),
  });
}

export function useDeleteBin() {
  const qc = useQueryClient();
  const { store } = useStore();
  return useMutation({
    mutationFn: (id: string) => {
      const path = useRealApi ? resolveDetailPath(`/bins/${id}`) : `/bins/${id}`;
      if (!path) return Promise.reject(new Error('Path not mapped'));
      return apiFetch<void>(path, { method: 'DELETE' });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mdKeys.bins(store) }),
  });
}

// ─── Dispensing Stations ───────────────────────────────────────────────────

export function useStations() {
  const { store } = useStore();
  const resolved = resolvePath('/stations');
  return useQuery({
    queryKey: mdKeys.stations(store),
    queryFn: async () => {
      if (!resolved) return [];
      const query = useRealApi ? { store_id: store === 'ALL' ? undefined : store } : { store };
      const data = await apiFetchList<unknown>(resolved.path, { query });
      if (useRealApi) return unwrapPage(data).map(stationDtoToStation);
      return unwrapPage(data) as Station[];
    },
    enabled: resolved !== null,
  });
}

export interface StationInput {
  code: string;
  label: string;
  store_id?: string;
  location_type?: string;
  barcode_value?: string;
  capacity?: number;
  max_hold_minutes?: number | null;
}

export function useCreateStation() {
  const qc = useQueryClient();
  const { store } = useStore();
  return useMutation({
    mutationFn: (station: StationInput) => {
      const path = useRealApi
        ? (resolvePath('/stations')?.path ?? '/api/master-data/stations/')
        : '/stations';
      return apiFetch<Station>(path, {
        method: 'POST',
        body: { ...station, store_id: station.store_id || store },
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mdKeys.stations(store) }),
  });
}

export function useUpdateStation() {
  const qc = useQueryClient();
  const { store } = useStore();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<StationInput> }) => {
      const path = useRealApi ? resolveDetailPath(`/stations/${id}`) : `/stations/${id}`;
      if (!path) return Promise.reject(new Error('Path not mapped'));
      return apiFetch<Station>(path, { method: 'PATCH', body: patch });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mdKeys.stations(store) }),
  });
}

export function useDeleteStation() {
  const qc = useQueryClient();
  const { store } = useStore();
  return useMutation({
    mutationFn: (id: string) => {
      const path = useRealApi ? resolveDetailPath(`/stations/${id}`) : `/stations/${id}`;
      if (!path) return Promise.reject(new Error('Path not mapped'));
      return apiFetch<void>(path, { method: 'DELETE' });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mdKeys.stations(store) }),
  });
}

export interface StationLabel {
  id: string;
  code: string;
  label: string;
  location_type: string;
  barcode_value: string;
}

/** Fetch the barcode/QR payload for a dispense-location label (live API). */
export function usePrintStationLabel() {
  return useMutation({
    mutationFn: (id: string): Promise<StationLabel> => {
      if (!useRealApi) return Promise.reject(new Error('Label printing needs the live API'));
      return apiFetch<StationLabel>(`/api/master-data/stations/${id}/print_label/`);
    },
  });
}

// ─── Inventory Policies ────────────────────────────────────────────────────

export function usePolicies(storeId?: string) {
  const resolved = resolvePath('/policies');
  return useQuery({
    queryKey: [...mdKeys.policies(), storeId ?? 'all'],
    queryFn: async () => {
      if (!resolved) return [];
      const query: Record<string, string | number> = {};
      if (storeId && storeId !== 'ALL') query.store_id = storeId;
      const data = await apiFetchList<unknown>(resolved.path, { query });
      return unwrapPage(data) as Policy[];
    },
    enabled: resolved !== null,
  });
}

export function useResolvePolicy(storeId: string | undefined) {
  return useQuery({
    queryKey: ['master-data', 'policy-resolve', storeId ?? 'default'],
    queryFn: () =>
      apiFetch<Policy>('/api/master-data/policies/resolve/', {
        query: storeId ? { store_id: storeId } : undefined,
      }),
    enabled: useRealApi,
  });
}

export function useUpdatePolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<Policy> }) => {
      const path = useRealApi ? resolveDetailPath(`/policies/${id}`) : `/policies/${id}`;
      if (!path) return Promise.reject(new Error('Path not mapped'));
      return apiFetch<Policy>(path, { method: 'PATCH', body: patch });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mdKeys.policies() }),
  });
}

export function useCreatePolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<Policy>) => {
      const path = useRealApi
        ? (resolvePath('/policies')?.path ?? '/api/master-data/policies/')
        : '/policies';
      return apiFetch<Policy>(path, { method: 'POST', body: payload });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mdKeys.policies() }),
  });
}

// ─── Bulk Import Jobs ──────────────────────────────────────────────────────

export function useBulkJobs() {
  const importPath = resolvePath('/bulk-jobs');
  const exportPath = resolvePath('/bulk-exports');
  return useQuery({
    queryKey: mdKeys.bulkJobs(),
    queryFn: async () => {
      const [importData, exportData] = await Promise.all([
        importPath ? apiFetchList<unknown>(importPath.path) : Promise.resolve([]),
        exportPath ? apiFetchList<unknown>(exportPath.path) : Promise.resolve([]),
      ]);
      const imports = (unwrapPage(importData) as BulkJob[]).map((j) => ({
        ...j,
        _kind: 'import' as const,
      }));
      const exports = (unwrapPage(exportData) as BulkJob[]).map((j) => ({
        ...j,
        success_rows: j.success_rows ?? j.total_rows ?? 0,
        error_rows: j.error_rows ?? 0,
        _kind: 'export' as const,
      }));
      return [...imports, ...exports].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
    },
    enabled: importPath !== null || exportPath !== null,
  });
}

export function useTriggerBulkImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { payload_csv: string; store_id?: string }) =>
      apiFetch<BulkJob>('/api/master-data/catalog/bulk-import/', {
        method: 'POST',
        body: { payload_csv: payload.payload_csv },
        query: payload.store_id ? { store_id: payload.store_id } : undefined,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: mdKeys.bulkJobs() }),
  });
}

export function useProcessBulkJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) =>
      apiFetch<BulkJob>(`/api/master-data/bulk-jobs/${jobId}/process/`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: mdKeys.bulkJobs() }),
  });
}

export function useCatalogTemplate() {
  return useQuery({
    queryKey: ['master-data', 'catalog-template'],
    queryFn: () => apiFetch<{ columns: string[] }>('/api/master-data/catalog/template/'),
    enabled: useRealApi,
  });
}

export function useCatalogExport() {
  return useMutation({
    mutationFn: async () => {
      const res = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/api/master-data/catalog/bulk-export/`,
        { headers: { Accept: 'text/csv' } },
      );
      return res.text();
    },
  });
}

// ─── Bulk Import V2 (Celery-backed) ───────────────────────────────────────

export type ImportJobType =
  | 'store_type'
  | 'bin_type'
  | 'store'
  | 'bin_location'
  | 'user'
  | 'catalog'
  | 'stock';
export type ImportMode = 'dry_run' | 'write';

export interface BulkImportJobV2 {
  id: string;
  job_type: ImportJobType;
  mode: ImportMode;
  status: string;
  total_rows: number;
  success_rows: number;
  error_rows: number;
  summary: unknown;
  created_at: string;
}

export interface BulkExportJob {
  id: string;
  job_type: ImportJobType;
  status: string;
  total_rows: number;
  download_url: string | null;
  created_at: string;
}

export interface ImportProgress {
  pct: number;
  message: string;
  status?: string;
  download_url?: string;
  summary?: Record<string, unknown>;
  error_details?: unknown[];
}

export function useUploadBulkImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      file: File;
      job_type: ImportJobType;
      mode: ImportMode;
      store_id?: string;
    }) => {
      const formData = new FormData();
      formData.append('file', payload.file);
      formData.append('job_type', payload.job_type);
      formData.append('mode', payload.mode);
      if (payload.store_id) formData.append('store_id', payload.store_id);

      const token = await getAuthToken();
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(
        `${import.meta.env.VITE_API_BASE_URL ?? ''}/api/master-data/bulk-jobs/upload/`,
        { method: 'POST', headers, body: formData },
      );
      if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
      return res.json() as Promise<BulkImportJobV2>;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mdKeys.bulkJobs() }),
  });
}

export function useImportProgress(jobId: string | null) {
  return useQuery({
    queryKey: ['master-data', 'import-progress', jobId],
    queryFn: () => apiFetch<ImportProgress>(`/api/master-data/bulk-jobs/${jobId}/progress/`),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const d = query.state.data;
      if (!d) return 1500;
      if (d.pct >= 100 || d.status === 'completed' || d.status === 'failed') return false;
      return 1500;
    },
  });
}

export function useCreateExport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { job_type: ImportJobType }) =>
      apiFetch<BulkExportJob>('/api/master-data/bulk-exports/create/', {
        method: 'POST',
        body: payload,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['master-data', 'bulk-exports'] }),
  });
}

export function useExportProgress(jobId: string | null) {
  return useQuery({
    queryKey: ['master-data', 'export-progress', jobId],
    queryFn: () => apiFetch<ImportProgress>(`/api/master-data/bulk-exports/${jobId}/progress/`),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const d = query.state.data;
      if (!d) return 1500;
      if (d.pct >= 100 || d.status === 'completed' || d.status === 'failed') return false;
      return 1500;
    },
  });
}

export function useDownloadTemplate(modelName: ImportJobType | null, storeId?: string) {
  return useQuery({
    queryKey: ['master-data', 'template', modelName, storeId],
    queryFn: async () => {
      const token = await getAuthToken();
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      // store_id scopes store-specific dropdowns in the template (e.g. the stock bin_code column).
      const qs = storeId ? `?store_id=${encodeURIComponent(storeId)}` : '';
      const res = await fetch(
        `${import.meta.env.VITE_API_BASE_URL ?? ''}/api/master-data/bulk-jobs/templates/${modelName}/${qs}`,
        { headers },
      );
      if (!res.ok) throw new Error('Template download failed');
      return res.blob();
    },
    enabled: false, // manual trigger
  });
}

// ─── Stores (Identity) ────────────────────────────────────────────────────

export function useMdStores() {
  const resolved = resolvePath('/stores');
  return useQuery({
    queryKey: mdKeys.stores(),
    queryFn: async (): Promise<Store[]> => {
      if (!resolved) return [];
      const data = await apiFetchList<unknown>(resolved.path);
      if (useRealApi) return unwrapPage(data).map(storeDtoToStore);
      return (data as { id: string; name: string }[]).map((s) => ({
        ...s,
        code: s.id,
        active: true,
      }));
    },
    enabled: resolved !== null,
  });
}

// ─── Store mutations ──────────────────────────────────────────────────────

interface StorePayload {
  name: string;
  code: string;
  store_type: string;
  address: string;
}

export function useCreateStore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: StorePayload) => {
      return apiFetch<unknown>('/api/identity/manage-stores/', {
        method: 'POST',
        body: payload,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mdKeys.stores() }),
  });
}

export function useUpdateStore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: StorePayload & { id: string }) => {
      return apiFetch<unknown>(`/api/identity/manage-stores/${id}/`, {
        method: 'PATCH',
        body: payload,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mdKeys.stores() }),
  });
}

export function useDeactivateStore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      return apiFetch<unknown>(`/api/identity/manage-stores/${id}/deactivate/`, {
        method: 'POST',
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mdKeys.stores() }),
  });
}

// ─── Manufacturers ────────────────────────────────────────────────────────

export function useManufacturers() {
  return useQuery({
    queryKey: mdKeys.manufacturers(),
    queryFn: async () => {
      if (useRealApi) {
        const data = await apiFetch<string[]>('/api/master-data/products/manufacturers/');
        return data;
      }
      // Mock mode: derive from catalog products
      const resolved = resolvePath('/products');
      if (!resolved) return [];
      const data = await apiFetchList<unknown>(resolved.path);
      const products = (Array.isArray(data) ? data : []) as { mfr?: string }[];
      const mfrs = new Set(products.map((p) => p.mfr).filter(Boolean));
      return Array.from(mfrs).sort() as string[];
    },
  });
}

// ─── Roles & RBAC Users ────────────────────────────────────────────────────

export interface RoleDto {
  id: string;
  code: string;
  name: string;
  is_active: boolean;
  member_count: number;
}

export interface RbacUserDto {
  id: string;
  username: string;
  email: string;
  is_active: boolean;
  role: string | null;
  store_scope: string[];
  avatar_url?: string | null;
}

export function useRoles() {
  const resolved = resolvePath('/roles');
  return useQuery({
    queryKey: ['identity', 'roles'],
    queryFn: async (): Promise<RoleDto[]> => {
      if (!resolved) return [];
      const data = await apiFetchList<unknown>(resolved.path);
      return unwrapPage(data) as RoleDto[];
    },
    enabled: resolved !== null,
  });
}

export function useRbacUsers() {
  const resolved = resolvePath('/rbac-users');
  return useQuery({
    queryKey: ['identity', 'rbac-users'],
    queryFn: async (): Promise<RbacUserDto[]> => {
      if (!resolved) return [];
      const data = await apiFetchList<unknown>(resolved.path);
      return unwrapPage(data) as RbacUserDto[];
    },
    enabled: resolved !== null,
  });
}

export function useAssignRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, roleCode }: { userId: string; roleCode: string }) =>
      apiFetch<unknown>(`/api/identity/users/${userId}/assign-role/`, {
        method: 'POST',
        body: { role_code: roleCode },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['identity', 'rbac-users'] }),
  });
}

export function useSuspendUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      apiFetch<unknown>(`/api/identity/users/${userId}/suspend/`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['identity', 'rbac-users'] }),
  });
}

export function useReinstateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      apiFetch<unknown>(`/api/identity/users/${userId}/reinstate/`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['identity', 'rbac-users'] }),
  });
}

export function useUpdateStoreScope() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, storeIds }: { userId: string; storeIds: string[] }) =>
      apiFetch<unknown>(`/api/identity/users/${userId}/store-scope/`, {
        method: 'POST',
        body: { store_ids: storeIds },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['identity', 'rbac-users'] }),
  });
}

export interface CreateUserInput {
  username: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  password: string;
  role: string; // role code, e.g. 'PH'
  store_ids?: string[];
  avatar?: string; // optional base64 data URL (cropped square)
}

/** Create a user in the caller's tenant (ADM only). The backend forces the tenant from the JWT. */
export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateUserInput) =>
      apiFetch<RbacUserDto>('/api/identity/users/create/', { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['identity', 'rbac-users'] }),
  });
}

// ─── Role Capability Grants ────────────────────────────────────────────────

export interface RoleCapabilityGrantDto {
  id: string;
  role_code: string;
  capability: string;
  is_granted: boolean;
}

export function useRoleCapabilities() {
  const resolved = resolvePath('/role-capabilities');
  return useQuery({
    queryKey: ['identity', 'role-capabilities'],
    queryFn: async (): Promise<RoleCapabilityGrantDto[]> => {
      if (!resolved) return [];
      const data = await apiFetchList<unknown>(resolved.path);
      return (Array.isArray(data) ? data : unwrapPage(data)) as RoleCapabilityGrantDto[];
    },
    enabled: resolved !== null,
  });
}

export function useToggleCapability() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roleCode, capability }: { roleCode: string; capability: string }) =>
      apiFetch<{ role_code: string; capability: string; is_granted: boolean }>(
        '/api/identity/role-capabilities/toggle/',
        { method: 'POST', body: { role_code: roleCode, capability } },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['identity', 'role-capabilities'] }),
  });
}

export function useSeedCapabilities() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ detail: string }>('/api/identity/role-capabilities/seed/', { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['identity', 'role-capabilities'] }),
  });
}
